import axios from 'axios'
import router from '@/router/router'
import { Message, MessageBox } from 'element-ui'
import store from '@/store'
import { getApiKey } from '@/utils/apiKey'

const CancelToken = axios.CancelToken
const source = CancelToken.source()
const baseURL = process.env.VUE_APP_AXIOS_BASE_URL || ''

// 运行时注入（评审 W6：源码/构建产物不再内置 x-api-key，部署时改 public/runtime-config.js）
const API_KEY = getApiKey()

function normalizeAuthToken (token) {
  if (!token) return ''
  const t = String(token).trim()
  if (!t) return ''
  if (t.toLowerCase().startsWith('bearer ')) {
    return t.slice(7).trim()
  }
  return t
}

function getAuthToken () {
  const fromStorage = normalizeAuthToken(localStorage.getItem('Authorization'))
  if (fromStorage) return fromStorage

  try {
    const rawUserInfo = localStorage.getItem('user_info')
    if (!rawUserInfo) return ''
    const userInfo = JSON.parse(rawUserInfo)
    return normalizeAuthToken(userInfo && userInfo.token)
  } catch (e) {
    return ''
  }
}

/** 与 axios 拦截器一致，供 el-upload 等非 axios 请求携带鉴权头 */
export function buildUploadAuthHeaders () {
  const token = getAuthToken()
  return {
    'x-api-key': API_KEY,
    Authorization: token,
    authorization: token
  }
}

const service = axios.create({
  baseURL,
  timeout: 1000 * 30
})

/** 将后端业务 code 规范为 number（无法解析时返回 NaN） */
export function toBusinessCode (code) {
  if (code === null || code === undefined) return NaN
  const n = Number(code)
  return Number.isFinite(n) ? n : NaN
}

/** 业务成功：与拦截器 normalize 后的 body.code===200 一致 */
export function isApiSuccess (res) {
  return res != null && typeof res === 'object' && toBusinessCode(res.code) === 200
}

/** 统一成功/失败响应体：兼容 message / msg，code 为 number */
export function normalizeResponseBody (data) {
  if (data == null || typeof data !== 'object') {
    return { code: NaN, message: '响应格式异常', data: null }
  }
  const code = toBusinessCode(data.code)
  const msg =
    data.message != null && String(data.message).trim() !== ''
      ? String(data.message)
      : (data.msg != null ? String(data.msg) : '')
  return Object.assign({}, data, {
    code: Number.isFinite(code) ? code : data.code,
    message: msg || '请求失败'
  })
}

function clearAuthAndGoLogin () {
  localStorage.removeItem('Authorization')
  localStorage.removeItem('user_info')
  router.push('/login')
}

function isForbiddenCode (body) {
  if (body == null || typeof body !== 'object') return false
  const c = toBusinessCode(body.code)
  return c === 403 || String(body.code) === '403'
}

service.interceptors.request.use(
  (config) => {
    const token = getAuthToken()
    config.headers['x-api-key'] = API_KEY
    config.headers.Authorization = token
    config.headers.authorization = token
    return config
  },
  (error) => Promise.reject(error)
)

service.interceptors.response.use(
  (response) => {
    // 二进制响应（PDF等）直接放行，不走业务码解析
    if (response.config && response.config.responseType === 'blob') {
      return response
    }
    const raw = response.data
    const res = normalizeResponseBody(raw)
    const code = toBusinessCode(res.code)

    if (Number.isFinite(code) && code === 200) {
      return res
    }

    if (code === 403 || String(res.code) === '403') {
      clearAuthAndGoLogin()
      store.commit('changeLoadingStatus', false)
      return Promise.reject(Object.assign({}, res, { code: 403, httpStatus: response.status }))
    }

    const msg = res.message || '请求失败'
    Message.error(msg)
    store.commit('changeLoadingStatus', false)
    return Promise.reject(Object.assign({}, res, { httpStatus: response.status }))
  },
  (error) => {
    store.commit('changeLoadingStatus', false)
    const resp = error.response

    if (!resp) {
      const msg =
        error.code === 'ECONNABORTED'
          ? '请求超时，请稍后重试'
          : (error.message === 'Network Error' ? '网络异常，请检查网络连接' : (error.message || '网络异常，请稍后重试'))
      Message.error(msg)
      return Promise.reject({
        code: 0,
        message: msg,
        httpStatus: null,
        raw: error
      })
    }

    const data = resp.data
    const body = typeof data === 'object' && data !== null ? data : {}
    if (isForbiddenCode(body)) {
      clearAuthAndGoLogin()
      return Promise.reject({
        code: 403,
        message: typeof body.message === 'string' ? body.message : '无权限',
        httpStatus: resp.status,
        raw: data
      })
    }

    if (store.getters.respErrMsgBoxMark === false) {
      const msg =
        typeof body.message === 'string' && body.message.trim() !== ''
          ? body.message
          : (typeof data === 'string' ? data : (resp.statusText || '请求失败'))
      MessageBox.alert(msg, String(resp.status || '错误'), {
        confirmButtonText: '确定',
        type: 'error',
        callback: () => {
          if (isForbiddenCode(body)) {
            clearAuthAndGoLogin()
          }
        }
      })
      store.commit('changeRespErrMsgBoxMark', true)
    }

    const code = toBusinessCode(body.code)
    return Promise.reject({
      code: Number.isFinite(code) ? code : resp.status,
      message: typeof body.message === 'string' ? body.message : (typeof data === 'string' ? data : '请求失败'),
      httpStatus: resp.status,
      raw: data
    })
  }
)

export function get (url, params) {
  return service({
    method: 'get',
    url,
    params,
    cancelToken: source.token
  })
}

export function fetch (url, params) {
  return service({
    method: 'get',
    url,
    params,
    cancelToken: source.token
  })
}

export function post (url, params) {
  return service({
    method: 'post',
    url,
    data: params,
    cancelToken: source.token
  })
}

// 二进制下载（PDF等）：返回 axios 原始 response（含 headers + blob data）
export function postBlob (url, params) {
  return service({
    method: 'post',
    url,
    data: params,
    responseType: 'blob',
    cancelToken: source.token
  })
}

export default service
